import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formatAmount } from '@/utils/formatAmount';
import { useCurrency } from '@/contexts/CurrencyContext';
import type { Donation } from '@/hooks/useDonations';

interface RealtimeTickerProps {
  donations: Donation[];
  latestDonation: Donation | null;
}

interface TickerItem {
  text: string;
  isNew: boolean;
}

const RealtimeTicker = ({ donations, latestDonation }: RealtimeTickerProps) => {
  const { t } = useTranslation();
  const { displayCurrency, convert } = useCurrency();
  const [tickerItems, setTickerItems] = useState<TickerItem[]>([]);
  const [flash, setFlash] = useState(false);
  const marqueeKey = useRef(0);

  useEffect(() => {
    // Get latest 10 donations sorted by time
    const recentDonations = [...(donations || [])]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 10);

    const items: TickerItem[] = recentDonations.map((d) => {
      // Convert to display currency
      const convertedAmount = convert(Number(d.amount), d.currency);
      const formattedAmount = formatAmount(convertedAmount, displayCurrency);
      
      const isNewest = latestDonation && d.id === latestDonation.id;
      const text = isNewest 
        ? t('ticker.newBurn', { name: d.display_name, amount: formattedAmount })
        : t('ticker.justBurned', { name: d.display_name, amount: formattedAmount });
      
      return { text, isNew: !!isNewest };
    });

    if (items.length === 0) {
      items.push({ text: t('ticker.waitingForFirst'), isNew: false });
      items.push({ text: t('ticker.beFirst'), isNew: false });
      items.push({ text: t('ticker.leaderboardWaits'), isNew: false });
    }

    setTickerItems(items);
  }, [donations, latestDonation, displayCurrency, convert, t]);

  // Flash effect when new donation comes in
  useEffect(() => {
    if (latestDonation) {
      setFlash(true);
      marqueeKey.current += 1; // Reset animation for new donation
      const timer = setTimeout(() => setFlash(false), 500);
      return () => clearTimeout(timer);
    }
  }, [latestDonation]);

  return (
    <footer 
      className={`fixed bottom-0 left-0 right-0 border-t-2 border-primary bg-background py-3 overflow-hidden transition-colors duration-300 ${flash ? 'bg-primary/20' : ''}`}
    >
      <div className="flex whitespace-nowrap">
        <div 
          key={marqueeKey.current}
          className="animate-marquee inline-flex gap-16"
          style={{
            animation: 'marquee 30s linear infinite',
          }}
        >
          {[...tickerItems, ...tickerItems].map((item, index) => (
            <span 
              key={index} 
              className={`text-sm md:text-base font-medium ${
                item.isNew 
                  ? 'text-yellow-400 font-bold animate-pulse' 
                  : 'text-primary'
              }`}
            >
              {item.text}
            </span>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 30s linear infinite;
        }
      `}</style>
    </footer>
  );
};

export default RealtimeTicker;
